import type { Task, TaskAction } from "@gw/shared-types";

export type DerivedTaskStatus = "Completed" | "InProgress" | "NotStarted" | "Discarded";

/** Same substring rule the backend uses for wasReopened — keeps Discard detection consistent. */
export function findDiscardAction(actions: TaskAction[]): TaskAction | undefined {
  return actions.find((a) => a.actionType.toLowerCase().includes("discard"));
}

/**
 * Derives the Task Status pill from action timestamps + the task-level completion timestamp,
 * NOT Phoenix's raw Status field (which can lag). Rules in priority order:
 *   1. task.wasDiscarded → Discarded. (Backend flag: raw status "Discarded" OR a discard action.)
 *   2. Genuinely untouched — no action started AND no action completed → Not started.
 *   3. Every action has actionCompletedDatetime AND a terminal-completion signal — either
 *      task.taskCompletedDatetime is non-null OR the terminal raw status is "Completed" (some task
 *      types never stamp taskCompletedDatetime) → Completed. Auto-completed system/document tasks
 *      carry a completion time but no start — they land here, not in Not started. A task with all
 *      actions done but no completion signal (e.g. awaiting approval) stays In progress.
 *   4. Otherwise → In progress.
 */
export function deriveTaskStatus(task: Task): DerivedTaskStatus {
  if (task.wasDiscarded) return "Discarded";
  const noneStarted = task.actions.every((a) => a.actionStartDatetime == null);
  const noneCompleted = task.actions.every((a) => a.actionCompletedDatetime == null);
  if (noneStarted && noneCompleted) return "NotStarted";
  const allCompleted = task.actions.every((a) => a.actionCompletedDatetime != null);
  const statusCompleted = task.status?.trim().toLowerCase() === "completed";
  return allCompleted && (task.taskCompletedDatetime != null || statusCompleted)
    ? "Completed"
    : "InProgress";
}

/**
 * True when a task contributes a real Wait/Execution/Duration value to the table — i.e. exactly
 * when its row shows numbers rather than "—". Discarded and not-started tasks are hidden, and a
 * task that hasn't completed yet shows "running". Keeping the totals to this set means the header
 * and footer totals reconcile with the visible rows.
 */
export function isCountedTask(t: Task): boolean {
  const status = deriveTaskStatus(t);
  return status !== "Discarded" && status !== "NotStarted" && t.completedDatetime != null;
}

export interface TaskTotals {
  waitBd: number;
  waitCd: number;
  execBd: number;
  execCd: number;
  idleBd: number;
  idleCd: number;
}

/**
 * Cumulative Idle + Wait + Execution across the counted tasks (Execution = task duration = Σ active
 * time; Idle = dead time before each task opened). Duration = idle + wait + execution. Because idle
 * fills the gaps between tasks, these totals reconcile to the request's elapsed Release→Complete
 * window for sequential tasks (approximately when tasks overlap).
 */
export function taskTotals(tasks: Task[]): TaskTotals {
  return tasks.filter(isCountedTask).reduce<TaskTotals>(
    (acc, t) => ({
      waitBd: acc.waitBd + t.waitBd,
      waitCd: acc.waitCd + t.waitCd,
      execBd: acc.execBd + t.durationBd,
      execCd: acc.execCd + t.durationCd,
      idleBd: acc.idleBd + t.idleBd,
      idleCd: acc.idleCd + t.idleCd,
    }),
    { waitBd: 0, waitCd: 0, execBd: 0, execCd: 0, idleBd: 0, idleCd: 0 },
  );
}

/** Tooltip shown next to the Total Task Wait/Execution labels. */
export const TOTALS_TIP =
  "Summed across tasks; excludes discarded and not-started tasks. Tasks can overlap, so this " +
  "won't equal the request's elapsed Total duration.";

/** Tooltip for the Idle column + the footer Duration total (which now includes idle). */
export const IDLE_TIP =
  "Idle = elapsed time before this task opened — no task was assigned (gaps between tasks, " +
  "re-issues, weekends/holidays). Idle + Wait + Execution = Duration; the column totals add up to " +
  "the request's Release→Complete window for sequential tasks (approximate when tasks overlap).";
